/**
 * @rogueworlds/client — remote (and local) player avatars.
 *
 * Deliberately cheap: one shared 0.6 x 1.8 x 0.6 m box geometry, one material per player (a
 * deterministic colour hashed from the player id, so a given player keeps the same colour across
 * sessions and machines), and one shared "visor" slab per renderer that reads as a facing
 * indicator and doubles as the local player's emissive marker. `upsert` moves and rotates the
 * existing avatar instead of rebuilding it, so a 20 Hz snapshot stream allocates nothing.
 *
 * Name labels are NOT implemented: a canvas sprite per player was the lowest-value item in the
 * package (the HUD owns identity) and would have cost one texture per player (convention 8).
 *
 * Position: `PlayerState.pos` is the player's FEET (packages/shared), so the box — whose origin is
 * its centre — is lifted by half its height. `yaw` is applied directly to `rotation.y`, i.e. the
 * avatar treats +Z as forward; if the authority's yaw convention turns out to be the camera's
 * (-Z forward), the fix is one sign on this line.
 */

import * as THREE from "three";

const AVATAR_WIDTH_M = 0.6;
const AVATAR_HEIGHT_M = 1.8;
const MARKER_SIZE_M = 0.16;
/** Marker height above the avatar's centre: roughly head height. */
const MARKER_OFFSET_Y_M = 0.55;
/** Sits on the front (+Z) face so `yaw` is visible from outside. */
const MARKER_OFFSET_Z_M = AVATAR_WIDTH_M / 2;

/** FNV-1a over the id, then two extra mix steps so nearby ids land far apart in hue. */
function hashId(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x2545f491);
  hash ^= hash >>> 13;
  return hash >>> 0;
}

/** Distinct, readable colour per id. Deterministic: no `Math.random`, same input -> same colour. */
function avatarColor(id: string): THREE.Color {
  const hash = hashId(id);
  const hue = (hash % 360) / 360;
  const saturation = 0.45 + ((hash >>> 9) % 25) / 100;
  const lightness = 0.5 + ((hash >>> 17) % 15) / 100;
  return new THREE.Color().setHSL(hue, saturation, lightness);
}

interface Avatar {
  readonly mesh: THREE.Mesh<THREE.BoxGeometry, THREE.MeshLambertMaterial>;
  readonly marker: THREE.Mesh<THREE.BoxGeometry, THREE.MeshBasicMaterial>;
  readonly material: THREE.MeshLambertMaterial;
}

export class PlayerRenderer {
  private readonly scene: THREE.Scene;
  private readonly boxGeometry: THREE.BoxGeometry;
  private readonly markerGeometry: THREE.BoxGeometry;
  private readonly localMarker: THREE.MeshBasicMaterial;
  private readonly remoteMarker: THREE.MeshBasicMaterial;
  private readonly avatars = new Map<string, Avatar>();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.boxGeometry = new THREE.BoxGeometry(AVATAR_WIDTH_M, AVATAR_HEIGHT_M, AVATAR_WIDTH_M);
    this.markerGeometry = new THREE.BoxGeometry(MARKER_SIZE_M, MARKER_SIZE_M * 0.6, MARKER_SIZE_M);
    // Unlit + not tone mapped, so the marker reads as an emissive signal rather than as a surface.
    this.localMarker = new THREE.MeshBasicMaterial({ color: 0xffe9a8, toneMapped: false });
    this.remoteMarker = new THREE.MeshBasicMaterial({ color: 0x23262e, toneMapped: false });
  }

  /** Creates, moves or re-colours one avatar. Allocation-free once the avatar exists. */
  upsert(id: string, pos: { readonly x: number; readonly y: number; readonly z: number }, yaw: number, isLocal: boolean): void {
    let avatar = this.avatars.get(id);
    if (avatar === undefined) {
      avatar = this.create(id);
      this.avatars.set(id, avatar);
    }
    avatar.mesh.position.set(pos.x, pos.y + AVATAR_HEIGHT_M / 2, pos.z);
    avatar.mesh.rotation.y = yaw;
    avatar.marker.material = isLocal ? this.localMarker : this.remoteMarker;
  }

  remove(id: string): void {
    const avatar = this.avatars.get(id);
    if (avatar === undefined) {
      return;
    }
    this.scene.remove(avatar.mesh);
    // The geometry is shared; only the per-player material belongs to this avatar.
    avatar.material.dispose();
    this.avatars.delete(id);
  }

  clear(): void {
    for (const id of Array.from(this.avatars.keys())) {
      this.remove(id);
    }
  }

  get count(): number {
    return this.avatars.size;
  }

  /** Frees every avatar and everything this renderer allocated (geometries and marker materials). */
  dispose(): void {
    this.clear();
    this.boxGeometry.dispose();
    this.markerGeometry.dispose();
    this.localMarker.dispose();
    this.remoteMarker.dispose();
  }

  private create(id: string): Avatar {
    // One material per player: the identity colour is the only per-player surface difference, and
    // it is disposed with the avatar.
    const material = new THREE.MeshLambertMaterial({ color: avatarColor(id) });
    const mesh = new THREE.Mesh(this.boxGeometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = false;

    const marker = new THREE.Mesh(this.markerGeometry, this.remoteMarker);
    marker.position.set(0, MARKER_OFFSET_Y_M, MARKER_OFFSET_Z_M);
    mesh.add(marker);

    this.scene.add(mesh);
    return { mesh, marker, material };
  }
}
